import { ApolloProvider } from '@apollo/client';
import { HydratedRouter } from 'react-router/dom';
import * as Sentry from '@sentry/react-router';
import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';
import { startTransition, StrictMode } from 'react';
import { hydrateRoot } from 'react-dom/client';
import { I18nextProvider, initReactI18next } from 'react-i18next';
import { getInitialNamespaces } from 'remix-i18next/client';
import i18n from '~/i18n';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { PostHogProvider } from '@posthog/react';
import { client } from './apollo/singleton';
import posthog from './posthog/client';
import performanceTracking from './analytics/performanceTracking';
import { NODE_ENV, SENTRY_DSN, WEBAPP_ROUTE_PREFIX } from './constants';

performanceTracking.init({
    dsn: SENTRY_DSN,
    integrations: [
        Sentry.browserTracingIntegration(),

        Sentry.replayIntegration(),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
});

// eslint-disable-next-line import/prefer-default-export
export const reactQueryClient = new QueryClient();

async function hydrate() {
    await i18next
        .use(initReactI18next)
        .use(LanguageDetector)
        .use(Backend)
        .init({
            ...i18n,
            ns: getInitialNamespaces(),
            backend: {
                loadPath: `${NODE_ENV === 'production' ? `${WEBAPP_ROUTE_PREFIX}/build` : `${WEBAPP_ROUTE_PREFIX}`}/locales/{{lng}}.json`,
                requestOptions: {
                    cache: 'no-store',
                },
            },
            detection: {
                order: ['htmlTag'],
                caches: [],
            },
        });

    startTransition(() => {
        hydrateRoot(
            document,
            <I18nextProvider i18n={i18next}>
                <StrictMode>
                    <PostHogProvider client={posthog}>
                        <QueryClientProvider client={reactQueryClient}>
                            <ApolloProvider client={client}>
                                <HydratedRouter />
                            </ApolloProvider>
                        </QueryClientProvider>
                    </PostHogProvider>
                </StrictMode>
            </I18nextProvider>
        );
    });
}

if (!window.hydrated) {
    window.hydrated = true;
    if (window.requestIdleCallback) {
        window.requestIdleCallback(hydrate);
    } else {
        // Safari doesn't support requestIdleCallback
        // https://caniuse.com/requestidlecallback
        window.setTimeout(hydrate, 1);
    }
}
